import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  const [year, month, day] = d.split("T")[0].split("-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateShort(d) {
  if (!d) return "";
  const date = new Date(d);
  return (date.getMonth()+1).toString().padStart(2,"0") + "/" +
         date.getDate().toString().padStart(2,"0") + "/" +
         date.getFullYear().toString().slice(-2);
}
function StatusBadge({ status }) {
  return <span className={`badge badge-${status}`}>{status}</span>;
}

// ─── Invoice Print (overlay data on top of QB template image) ────────────────
function printInvoice(inv) {
  if (!inv) return;

  const lineItems = inv.line_items || [];
  const fmtNum = (n) => n != null && n !== "" ? parseFloat(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
  const fmtCur = (n) => parseFloat(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

  const subtotal = parseFloat(inv.subtotal || 0);
  const salesTax = parseFloat(inv.sales_tax || 0);
  const total = subtotal + salesTax;
  const balance = parseFloat(inv.balance_due ?? inv.balance ?? 0);
  const paymentsApplied = Math.max(0, total - balance);

  const fmtAddr = (addr) => {
    if (!addr) return "";
    return String(addr).split("\n").filter(Boolean).join("<br>");
  };

  // Build line items rows
  const lineItemsHtml = lineItems.map(li => {
    const hasQty = li.quantity != null && li.quantity !== "";
    const hasRate = li.rate != null && li.rate !== "";
    if (!hasQty && !hasRate) {
      // Text-only line (order#, tickets, job# etc) - spans description col only
      return `<tr>
        <td></td>
        <td></td>
        <td style="font-size:8pt;">${li.description || ""}</td>
        <td></td>
        <td></td>
        <td></td>
      </tr>`;
    }
    return `<tr>
      <td style="text-align:center;font-size:8pt;">${fmtNum(li.quantity)}</td>
      <td style="font-size:8pt;">${li.mix_code || ""}</td>
      <td style="font-size:8pt;">${li.description || ""}</td>
      <td style="text-align:center;font-size:8pt;">${li.unit_of_measure || ""}</td>
      <td style="text-align:right;font-size:8pt;">${fmtNum(li.rate)}</td>
      <td style="text-align:right;font-size:8pt;">${fmtNum(li.amount)}</td>
    </tr>`;
  }).join("");

  // The template image is 1224x1584px rendered at 8.5in x 11in
  // Page is 816 CSS px wide x 1056 CSS px tall (at 96dpi screen)
  // All overlay positions are in percentage or absolute px on the 816x1056 canvas

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Invoice ${inv.invoice_number || ""}</title>
<style>
  @page { size: 8.5in 11in; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { background: #fff; }
  .page {
    width: 8.5in;
    height: 11in;
    position: relative;
    overflow: hidden;
    background: white;
  }
  .bg-image {
    position: absolute;
    top: 0; left: 0;
    width: 8.5in;
    height: 11in;
    display: block;
  }
  /* All overlay divs are absolute positioned on the page */
  .overlay {
    position: absolute;
    font-family: Arial, sans-serif;
    color: #000;
    font-size: 8.5pt;
    line-height: 1.25;
  }

  /* Invoice number - top right area */
  .inv-number {
    top: 0.75in;
    right: 0.52in;
    text-align: right;
    font-size: 11pt;
  }

  /* Bill To address */
  .bill-to {
    top: 1.52in;
    left: 0.55in;
    width: 3.1in;
    font-size: 8pt;
  }

  /* Ship To address */
  .ship-to {
    top: 1.52in;
    left: 3.75in;
    width: 3.1in;
    font-size: 8pt;
  }

  /* Info row values - top bar with REP/REFERENCE/INVOICE DATE/SHIP VIA/SHIP DATE/TERMS */
  .info-row {
    top: 2.85in;
    left: 0.52in;
    right: 0.52in;
    display: flex;
    font-size: 8pt;
  }
  .info-cell {
    flex: 1;
    padding: 0 4px;
    line-height: 1.4;
  }

  /* Line items table */
  .line-items {
    position: absolute;
    top: 3.35in;
    left: 0.52in;
    right: 0.52in;
    font-size: 8pt;
  }
  .line-items table {
    width: 100%;
    border-collapse: collapse;
  }
  .line-items td {
    padding: 1px 3px;
    vertical-align: top;
  }
  /* Column widths matching QB: SHIPPED / MIX CODE / DESCRIPTION / U/M / UNIT PRICE / EXTENDED */
  .line-items .c-sh   { width: 11%; }
  .line-items .c-mc   { width: 12%; }
  .line-items .c-ds   { width: 37%; }
  .line-items .c-um   { width: 6%; }
  .line-items .c-up   { width: 14%; text-align: right; }
  .line-items .c-ex   { width: 14%; text-align: right; }

  /* Totals box - bottom right */
  .totals {
    position: absolute;
    bottom: 0.42in;
    right: 0.52in;
    width: 2.85in;
    font-size: 8.5pt;
  }
  .totals table {
    width: 100%;
    border-collapse: collapse;
  }
  .totals td {
    padding: 2px 4px;
  }
  .totals .lbl { text-align: left; }
  .totals .val { text-align: right; }
  .totals .bal-row td { font-weight: bold; }
</style>
</head>
<body>
<div class="page">

  <!-- Background: the actual QB invoice template -->
  <img class="bg-image" src="/cowtown_invoice_template.png" alt="">

  <!-- Invoice Number -->
  <div class="overlay inv-number">${inv.invoice_number || ""}</div>

  <!-- Bill To -->
  <div class="overlay bill-to">${fmtAddr(inv.bill_address || inv.company_name || "")}</div>

  <!-- Ship To -->
  <div class="overlay ship-to">${fmtAddr(inv.ship_address || "")}</div>

  <!-- Info Row: REP / REFERENCE / INVOICE DATE / SHIP VIA / SHIP DATE / TERMS -->
  <div class="overlay info-row">
    <div class="info-cell">${inv.rep || ""}</div>
    <div class="info-cell">${inv.reference_number || ""}</div>
    <div class="info-cell">${fmtDateShort(inv.invoice_date)}</div>
    <div class="info-cell">${inv.ship_via || ""}</div>
    <div class="info-cell">${fmtDateShort(inv.ship_date)}</div>
    <div class="info-cell">${inv.terms || ""}</div>
  </div>

  <!-- Line Items -->
  <div class="line-items">
    <table>
      <colgroup>
        <col class="c-sh"><col class="c-mc"><col class="c-ds">
        <col class="c-um"><col class="c-up"><col class="c-ex">
      </colgroup>
      <tbody>${lineItemsHtml}</tbody>
    </table>
  </div>

  <!-- Totals -->
  <div class="totals">
    <table>
      <tr><td class="lbl">Sales Amount</td><td class="val">${fmtCur(subtotal)}</td></tr>
      <tr><td class="lbl">Sales Tax</td><td class="val">${fmtCur(salesTax)}</td></tr>
      <tr><td class="lbl">Invoice Total</td><td class="val">${fmtCur(total)}</td></tr>
      <tr><td class="lbl">Payments Applied</td><td class="val">${fmtCur(paymentsApplied)}</td></tr>
      <tr class="bal-row"><td class="lbl">Balance Due</td><td class="val">${fmtCur(balance)}</td></tr>
    </table>
  </div>

</div>
</body>
</html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

// ─── Invoice List ──────────────────────────────────────────────────────────────
export function InvoicesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();

  const jobId = searchParams.get("job_id");
  const jobName = searchParams.get("job_name");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (status) params.set("status", status);
    if (jobId) params.set("job_id", jobId);
    api.getInvoices(params.toString())
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [page, status, jobId]);

  useEffect(() => { load(); }, [load]);

  const STATUSES = ["", "open", "overdue", "partial", "paid"];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">
          Invoices{jobName ? ` — ${jobName}` : ""}
        </div>
        <div className="page-subtitle">View and download all invoices on your account.</div>
      </div>

      {jobId && (
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginRight: 6 }}>Filtered by job:</span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "var(--primary)", color: "#fff",
            borderRadius: 20, padding: "2px 10px", fontSize: "0.8rem"
          }}>
            {jobName || jobId}
            <button
              onClick={() => { setSearchParams({}); setPage(1); }}
              style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 0, fontSize: "1rem", lineHeight: 1 }}
            >×</button>
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {STATUSES.map(s => (
          <button
            key={s || "all"}
            className={`btn btn-sm ${status === s ? "btn-primary" : "btn-outline"}`}
            onClick={() => { setStatus(s); setPage(1); }}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        {loading
          ? <div className="loading">Loading invoices…</div>
          : data?.invoices?.length === 0
            ? (
              <div className="empty-state">
                <div className="empty-state-icon">◻</div>
                <p>No invoices found{status ? ` with status "${status}"` : ""}.</p>
              </div>
            )
            : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Invoice Date</th>
                        <th>Due Date</th>
                        <th>Status</th>
                        <th className="td-right">Amount</th>
                        <th className="td-right">Balance Due</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map(inv => (
                        <tr key={inv.id}>
                          <td style={{ fontWeight: 500 }}>{inv.invoice_number || "—"}</td>
                          <td>{fmtDate(inv.invoice_date)}</td>
                          <td style={{ color: inv.status === "overdue" ? "var(--danger)" : undefined }}>
                            {fmtDate(inv.due_date)}
                          </td>
                          <td>
                            <StatusBadge status={inv.status} />
                            {inv.has_dispute && <span className="badge badge-overdue" style={{marginLeft:6,fontSize:"0.65rem"}}>Disputed</span>}
                            {inv.has_ticket_request && <span className="badge badge-partial" style={{marginLeft:6,fontSize:"0.65rem"}}>Tickets Requested</span>}
                          </td>
                          <td className="td-right td-mono">{fmt(inv.amount)}</td>
                          <td className="td-right td-mono" style={{ fontWeight: 600 }}>
                            {fmt(inv.balance_due)}
                          </td>
                          <td>
                            <Link to={`/invoices/${inv.id}`} className="btn btn-outline btn-sm">View</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.pagination && data.pagination.pages > 1 && (
                  <div className="pagination">
                    <span>
                      Page {data.pagination.page} of {data.pagination.pages}
                      &nbsp;·&nbsp; {data.pagination.total} invoices
                    </span>
                    <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                    <button className="btn btn-outline btn-sm" disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
                  </div>
                )}
              </>
            )
        }
      </div>
    </div>
  );
}

// ─── Invoice Detail ────────────────────────────────────────────────────────────
export function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeText, setDisputeText] = useState("");
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const isPreview = localStorage.getItem("ar_preview_mode") === "true";

  useEffect(() => {
    api.getInvoice(id)
      .then(setInvoice)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    api.getInvoiceStatus(id).then(s => { setStatusInfo(s); setAttachments(s.attachments || []); }).catch(() => {});
  }, [id]);

  function parseLineItems(rawItems) {
    const regular = [];
    let orderNumber = null;
    let ticketNumbers = [];
    let jobNumber = null;
    for (const line of rawItems) {
      const desc = (line.description || "").trim();
      const hasAmount = line.quantity || line.rate || line.amount;
      if (hasAmount) { regular.push(line); continue; }
      const orderMatch = desc.match(/Order\s+(\d+)/i);
      if (orderMatch) { orderNumber = orderMatch[1]; continue; }
      if (/^[\d,\s]+$/.test(desc)) {
        const nums = desc.split(",").map(s => s.trim()).filter(s => /^\d+$/.test(s));
        if (nums.length > 0) { ticketNumbers.push(...nums); continue; }
      }
      const jobMatch = desc.match(/Job#?\s*(\d+)/i);
      if (jobMatch) { jobNumber = jobMatch[1]; continue; }
      if (/^Job#?\s*$/i.test(desc)) { continue; }
      regular.push(line);
    }
    return { regular, orderNumber, ticketNumbers, jobNumber };
  }

  function fmtQty(v) {
    if (!v) return "";
    const n = parseFloat(v);
    return isNaN(n) ? v : n % 1 === 0 ? n.toString() : n.toFixed(2);
  }

  async function handleDownload() {
    setDownloading(true);
    try { window.print(); } finally { setDownloading(false); }
  }

  async function submitDispute() {
    if (!disputeText.trim()) return;
    setDisputeSubmitting(true);
    try {
      await api.submitDispute(id, { description: disputeText });
      setDisputeOpen(false); setDisputeText("");
      const s = await api.getInvoiceStatus(id); setStatusInfo(s);
    } catch (err) { alert("Failed to submit dispute: " + err.message); }
    finally { setDisputeSubmitting(false); }
  }

  async function submitTicketRequest() {
    setTicketSubmitting(true);
    try {
      await api.requestTickets(id);
      setTicketOpen(false);
      const s = await api.getInvoiceStatus(id); setStatusInfo(s);
    } catch (err) { alert("Failed to request tickets: " + err.message); }
    finally { setTicketSubmitting(false); }
  }

  async function handleRequestStandard() {
    setRequesting(true);
    try {
      await api.requestOfficialInvoice(id);
      setRequested(true);
    } catch (e) { alert("Failed to send request. Please email ssmith@cowtownrm.us directly."); }
    finally { setRequesting(false); }
  }

  if (loading) return <div className="loading">Loading invoice…</div>;
  if (error)   return <div className="alert alert-error">{error}</div>;
  if (!invoice) return null;

  const rawItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const { regular, orderNumber, ticketNumbers, jobNumber } = parseLineItems(rawItems);
  const reference = invoice.reference_number || jobNumber || "—";
  const subtotal = parseFloat(invoice.subtotal || 0);
  const salesTax = parseFloat(invoice.sales_tax || 0);
  const total = subtotal + salesTax;
  const balance = parseFloat(invoice.balance_due ?? invoice.balance ?? 0);
  const paid = Math.max(0, total - balance);
  const dispute = statusInfo?.dispute;
  const ticket = statusInfo?.ticket_request;

  const LOGO_URL = "/logo.png";
  const REMIT = { name: "CowTown Redi Mix, Inc.", po: "P.O. Box 162327", city: "Fort Worth, TX 76161", office: "817-759-1919", dispatch: "817-759-2599", website: "cowtownrm.com" };
  const statusClass = { paid: "badge-paid", partial: "badge-partial", overdue: "badge-overdue", open: "badge-open" }[invoice.status] || "badge-open";

  return (
    <div>
      {/* Toolbar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}} className="no-print">
        <button className="btn-link" style={{fontSize:"0.85rem"}} onClick={() => navigate(-1)}>← Back to invoices</button>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {!isPreview && !dispute && <button className="btn btn-outline" onClick={() => setDisputeOpen(true)}>Dispute Invoice</button>}
          {!isPreview && !ticket && <button className="btn btn-outline" onClick={() => setTicketOpen(true)}>Request Tickets</button>}
          {!isPreview && (requested
            ? <span style={{fontSize:"0.88rem",color:"var(--paid)",fontWeight:600}}>✓ Request sent</span>
            : <button className="btn btn-outline" onClick={handleRequestStandard} disabled={requesting}>{requesting ? "Sending..." : "Request Standard Invoice"}</button>
          )}
          <button className="btn btn-primary" onClick={handleDownload} disabled={downloading}>⬇ Download / Print</button>
        </div>
      </div>



      {/* Invoice sheet */}
      <div className="inv-sheet" style={{background:"#fff",border:"1px solid var(--border)",borderRadius:8,padding:"40px 48px",boxShadow:"var(--shadow)"}}>

        {/* Header */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:24,alignItems:"start",marginBottom:28,paddingBottom:24,borderBottom:"2px solid var(--blue)"}}>
          <div><img src={LOGO_URL} alt="Cow Town Redi Mix" style={{width:140,height:"auto",display:"block"}} /></div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:"0.65rem",fontWeight:700,letterSpacing:"0.12em",color:"var(--text-muted)",marginBottom:4}}>REMIT TO</div>
            <div style={{fontWeight:700,color:"var(--blue)",fontSize:"1rem",marginBottom:2}}>{REMIT.name}</div>
            <div style={{fontSize:"0.82rem",lineHeight:1.65}}>{REMIT.po}</div>
            <div style={{fontSize:"0.82rem",lineHeight:1.65}}>{REMIT.city}</div>
            <div style={{fontSize:"0.82rem",lineHeight:1.65}}>{REMIT.website}</div>
          </div>
          <div style={{textAlign:"right"}}>
            {[["Office",REMIT.office],["Dispatch",REMIT.dispatch]].map(([l,v]) => (
              <div key={l} style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:4}}>
                <span style={{fontSize:"0.68rem",fontWeight:700,letterSpacing:"0.08em",color:"var(--text-muted)",textTransform:"uppercase",paddingTop:2}}>{l}</span>
                <span style={{fontSize:"0.9rem",fontWeight:600}}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Title bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--blue)",color:"#fff",padding:"12px 20px",borderRadius:6,marginBottom:20}}>
          <div>
            <span style={{fontSize:"0.7rem",letterSpacing:"0.15em",fontWeight:700,opacity:0.8}}>INVOICE </span>
            <span style={{fontSize:"1.4rem",fontWeight:700}}>#{invoice.invoice_number}</span>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <span className={"badge " + statusClass}>{invoice.status ? invoice.status.toUpperCase() : ""}</span>
            {dispute && dispute.status !== "resolved" && (
              <span className="badge" style={{
                background: dispute.status==="reviewing" ? "#DBEAFE" : "#FEF3C7",
                color: dispute.status==="reviewing" ? "#1E40AF" : "#92400E"
              }}>
                DISPUTE {dispute.status.toUpperCase()}
              </span>
            )}
            {dispute && dispute.status === "resolved" && dispute.customer_note && (
              <span className="badge badge-paid">DISPUTE RESOLVED</span>
            )}
            {ticket && ticket.status === "pending" && (
              <span className="badge badge-partial">TICKETS REQUESTED</span>
            )}
            {ticket && ticket.status === "fulfilled" && (
              <span className="badge badge-paid">TICKETS AVAILABLE</span>
            )}
          </div>
        </div>

        {/* Addresses */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginBottom:20}}>
          {[["BILL TO", invoice.bill_address || invoice.company_name],["SHIP TO / JOB SITE", invoice.ship_address]].map(([label, addr]) => (
            <div key={label} style={{background:"var(--bg)",borderRadius:6,padding:"14px 16px"}}>
              <div style={{fontSize:"0.65rem",fontWeight:700,letterSpacing:"0.12em",color:"var(--blue)",marginBottom:6,textTransform:"uppercase"}}>{label}</div>
              <div style={{fontSize:"0.88rem",lineHeight:1.65}}>
                {addr ? addr.split("\n").map((l,i) => <div key={i}>{l}</div>) : <div>—</div>}
              </div>
            </div>
          ))}
        </div>

        {/* Meta row */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",border:"1px solid var(--border)",borderRadius:6,overflow:"hidden",marginBottom:16}}>
          {[
            {label:"REP", value:invoice.rep||"—"},
            {label:"REFERENCE", value:reference},
            {label:"ORDER #", value:orderNumber||"—"},
            {label:"INVOICE DATE", value:fmtDate(invoice.invoice_date)},
            {label:"DUE DATE", value:fmtDate(invoice.due_date)},
            {label:"TERMS", value:invoice.terms||"—"},
            {label:"SHIP VIA", value:invoice.ship_via||"—"},
          ].map((item, i, arr) => (
            <div key={item.label} style={{padding:"10px 12px",borderRight: i < arr.length-1 ? "1px solid var(--border)" : "none"}}>
              <div style={{fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.1em",color:"var(--text-muted)",textTransform:"uppercase",marginBottom:4}}>{item.label}</div>
              <div style={{fontSize:"0.83rem",fontWeight:600}}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Tickets */}
        {ticketNumbers.length > 0 && (
          <div style={{background:"var(--blue-lt)",borderLeft:"3px solid var(--blue)",borderRadius:4,padding:"8px 14px",marginBottom:16,fontSize:"0.85rem"}}>
            <span style={{fontWeight:700,color:"var(--blue)",marginRight:8,fontSize:"0.72rem",letterSpacing:"0.08em",textTransform:"uppercase"}}>DELIVERY TICKETS:</span>
            <span>{ticketNumbers.join(", ")}</span>
          </div>
        )}

        {/* Line items */}
        {regular.length > 0 ? (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.85rem",marginBottom:0}}>
            <thead>
              <tr style={{background:"var(--blue)",color:"#fff"}}>
                {[["ITEM","110px","left"],["DESCRIPTION","","left"],["QTY","65px","right"],["UNIT PRICE","100px","right"],["AMOUNT","110px","right"]].map(([h,w,a]) => (
                  <th key={h} style={{padding:"9px 12px",textAlign:a,fontSize:"0.63rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",width:w||"auto"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {regular.map((line, i) => {
                const isText = !line.quantity && !line.rate && !line.amount;
                return (
                  <tr key={i} style={{background: i%2===0 ? "#fff" : "#fafafa"}}>
                    <td style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",fontWeight:600}}>{line.mix_code || ""}</td>
                    <td style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",color: isText ? "var(--text-muted)" : "inherit",fontStyle: isText ? "italic" : "normal"}}>{line.description || ""}</td>
                    <td style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",textAlign:"right"}}>{isText ? "" : fmtQty(line.quantity)}</td>
                    <td style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",textAlign:"right"}}>{isText ? "" : (line.rate ? fmt(line.rate) : "")}</td>
                    <td style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",textAlign:"right",fontWeight:600}}>{isText ? "" : (line.amount ? fmt(line.amount) : "")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{background:"var(--bg)",borderRadius:6,padding:24,textAlign:"center",color:"var(--text-muted)",fontSize:"0.88rem"}}>
            Line item detail not available for this invoice.
          </div>
        )}

        {/* Totals */}
        <div style={{display:"flex",justifyContent:"flex-end",borderTop:"1px solid var(--border)",paddingTop:16,marginBottom:20}}>
          <div style={{width:280}}>
            {[["Sales Amount",fmt(subtotal)],["Sales Tax",fmt(salesTax)],["Invoice Total",fmt(total)],["Payments Applied","("+fmt(paid)+")"]].map(([l,v]) => (
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:"0.88rem",borderBottom:"1px solid var(--border)"}}>
                <span style={{color:"var(--text-muted)"}}>{l}</span><span>{v}</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",fontWeight:700,fontSize:"1rem",color:"var(--blue)"}}>
              <span>Balance Due</span><span>{fmt(balance)}</span>
            </div>
          </div>
        </div>

        {invoice.memo && <div style={{fontSize:"0.82rem",color:"var(--text-muted)",borderTop:"1px solid var(--border)",paddingTop:12,marginBottom:12}}><strong>Notes: </strong>{invoice.memo}</div>}
        {dispute && dispute.customer_note && (
          <div style={{fontSize:"0.82rem",color:"#065F46",background:"#D1FAE5",borderRadius:6,padding:"8px 12px",marginBottom:20}} className="no-print">
            <strong>Dispute Resolution: </strong>{dispute.customer_note}
          </div>
        )}

        {/* Footer note */}
        <div style={{background:"var(--blue-lt)",borderRadius:6,padding:"14px 18px",fontSize:"0.85rem",borderLeft:"3px solid var(--blue)"}} className="no-print">
          <p style={{margin:0}}>
            This is a summary invoice for your records.{" "}
            {requested
              ? <strong>Your request for the standard invoice has been sent — our team will email it to you shortly.</strong>
              : <>Need the standard invoice?{" "}
                  <button style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:"inherit",padding:0,textDecoration:"underline",fontFamily:"inherit"}} onClick={handleRequestStandard} disabled={requesting}>
                    {requesting ? "Sending..." : "Click here to request it"}
                  </button>{" "}and our team will email it to you.</>
            }
          </p>
        </div>
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="card no-print" style={{marginTop:20}}>
          <div className="card-header"><span className="card-title">Attachments</span></div>
          <div className="card-body">
            {attachments.map(att => (
              <div key={att.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:"1px solid var(--border)"}}>
                <span style={{flex:1,fontSize:"0.875rem"}}>📄 {att.filename}</span>
                <button className="btn btn-outline btn-sm" onClick={async () => {
                  try {
                    const token = localStorage.getItem("ar_token");
                    const res = await fetch("/api/invoices/"+id+"/attachment/"+att.id, {
                      headers: { "Authorization": "Bearer " + token }
                    });
                    const arrayBuffer = await res.arrayBuffer();
                    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = att.filename; a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  } catch(e) { alert("Download failed."); }
                }}>Download</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dispute modal */}
      {disputeOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:480,maxWidth:"90vw",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
            <h3 style={{marginBottom:12}}>Dispute Invoice {invoice.invoice_number}</h3>
            <p style={{color:"var(--text-muted)",fontSize:"0.875rem",marginBottom:16}}>Please describe the issue. Our team will review and follow up.</p>
            <textarea value={disputeText} onChange={e => setDisputeText(e.target.value)} placeholder="Describe the dispute..." rows={5}
              style={{width:"100%",padding:10,border:"1px solid var(--border)",borderRadius:6,fontSize:"0.875rem",fontFamily:"inherit",resize:"vertical"}} />
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
              <button className="btn btn-outline" onClick={() => {setDisputeOpen(false);setDisputeText("");}}>Cancel</button>
              <button className="btn btn-primary" onClick={submitDispute} disabled={disputeSubmitting||!disputeText.trim()}>
                {disputeSubmitting ? "Submitting..." : "Submit Dispute"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket modal */}
      {ticketOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:420,maxWidth:"90vw",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
            <h3 style={{marginBottom:12}}>Request Delivery Tickets</h3>
            <p style={{color:"var(--text-muted)",fontSize:"0.875rem",marginBottom:16}}>This will notify our billing team to upload the delivery tickets for invoice {invoice.invoice_number}.</p>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn btn-outline" onClick={() => setTicketOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitTicketRequest} disabled={ticketSubmitting}>
                {ticketSubmitting ? "Requesting..." : "Request Tickets"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; margin: 0; }
          .inv-sheet { border: none !important; box-shadow: none !important; padding: 16px 20px !important; }
          table { font-size: 0.75rem !important; }
          table th, table td { padding: 4px 8px !important; }
        }
        @page { size: letter portrait; margin: 0.4in; }
        .badge { display:inline-block; padding:3px 10px; border-radius:20px; font-size:0.7rem; font-weight:700; letter-spacing:0.08em; }
        .badge-paid    { background:#d4edda; color:#155724; }
        .badge-open    { background:#cfe2ff; color:#084298; }
        .badge-overdue { background:#f8d7da; color:#842029; }
        .badge-partial { background:#fff3cd; color:#664d03; }
      `}</style>
    </div>
  );
}
